import React, { useState, useEffect } from "react";
import { Menu, X, ChevronDown, Mail } from "lucide-react";
import bkLogo2 from "./pictures/bk_logo_no_text.png";
import imgAutomation1 from "./Photos_Website/Automation1.JPG";
import imgCad1 from "./Photos_Website/CAD1.jpg";
import imgCad2 from "./Photos_Website/CAD2.jpg";
import imgCad3 from "./Photos_Website/Cad Image 3.png";
import imgHmi from "./Photos_Website/HMI.jpg";
import imgMachine from "./Photos_Website/Machine.png";
import imgMcc1 from "./Photos_Website/MCC Cabinet 1.png";
import imgMcc3 from "./Photos_Website/MCC Cabinet 3.JPG";
import imgPlant from "./Photos_Website/plant.jpg";
import imgScada1 from "./Photos_Website/scada1.jpg";
import imgScada2 from "./Photos_Website/scada2.jpg";

// Color scheme - easily customizable
const colors = {
  primary: "#060a14", // Deep navy/black
  secondary: "#101d38", // Dark blue
  accent: "#a0bcf3", // Medium blue
  tertiary: "#004d6c", // Teal grey
  light: "#eff0f2", // Light grey
  white: "#ffffff",
};

const carouselSlides = [
  { url: imgAutomation1, caption: "Industrial Automation", bgColor: "#e8eaed" },
  { url: imgPlant, caption: "Plant Operations", bgColor: "#1a1f2e" },
  { url: imgMachine, caption: "Custom Machine Design", bgColor: "#ffffff" },
  { url: imgMcc1, caption: "MCC Panel Assembly", bgColor: "#dce4ed" },
  { url: imgMcc3, caption: "Electrical Control Cabinets", bgColor: "#dce4ed" },
  { url: imgHmi, caption: "HMI & Operator Interfaces", bgColor: "#f5f5f5" },
  { url: imgScada1, caption: "SCADA Systems", bgColor: "#f5f5f5" },
  { url: imgScada2, caption: "Process Monitoring", bgColor: "#f5f5f5" },
  { url: imgCad1, caption: "Electrical Schematics", bgColor: "#ffffff" },
  { url: imgCad2, caption: "3D Modelling & Drafting", bgColor: "#ffffff" },
  { url: imgCad3, caption: "Precision Engineering", bgColor: "#ffffff" },
];

export default function BKAutomation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null | "success" | "error"
  const [highlightForm, setHighlightForm] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto-advance carousel every 4 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["home", "about", "services", "contact"];
      const navEl = document.querySelector("nav");
      const navHeight = navEl ? navEl.offsetHeight : 90;
      // Use the same offset as scrollToSection so detection aligns exactly
      const scrollPosition = window.scrollY + navHeight + 1;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navEl = document.querySelector("nav");
      const navHeight = navEl ? navEl.offsetHeight : 90;
      const top =
        element.getBoundingClientRect().top + window.scrollY - navHeight;
      window.scrollTo({ top, behavior: "smooth" });
      setIsMenuOpen(false);
    }
  };

  const handleServiceClick = (serviceTitle, subjectLine) => {
    setFormData((prev) => ({ ...prev, subject: subjectLine }));
    scrollToSection("contact");
    // Small delay so the scroll starts before the glow fires
    setTimeout(() => {
      setHighlightForm(true);
      setTimeout(() => setHighlightForm(false), 600);
    }, 600);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus("success");
        setFormData({
          name: "",
          company: "",
          email: "",
          subject: "",
          message: "",
        });
      } else {
        setSubmitStatus("error");
      }
    } catch {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
        backgroundColor: colors.light,
        color: colors.primary,
      }}
    >
      {/* Navigation */}
      <nav
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: colors.white,
          borderBottom: `2px solid ${colors.light}`,
          boxShadow: "0 2px 16px rgba(6,10,20,0.08)",
          zIndex: 1000,
          transition: "all 0.3s ease",
        }}
      >
        <div
          className="nav-inner"
          style={{
            maxWidth: "1400px",
            margin: "0 auto",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Logo */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
            }}
          >
            <img
              src={bkLogo2}
              alt="BK Automation"
              className="nav-logo"
              style={{
                width: "auto",
                objectFit: "contain",
              }}
            />
          </div>
          {/* Desktop Navigation */}
          <div
            style={{
              display: "none",
              gap: "2.5rem",
              "@media (min-width: 768px)": { display: "flex" },
            }}
            className="desktop-nav"
          >
            {["home", "about", "services", "contact"].map((section) => (
              <button
                key={section}
                onClick={() => scrollToSection(section)}
                style={{
                  background: "none",
                  border: "none",
                  color:
                    activeSection === section
                      ? colors.tertiary
                      : colors.primary,
                  fontSize: "1rem",
                  fontWeight: "600",
                  cursor: "pointer",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  padding: "0.5rem 0",
                  position: "relative",
                  transition: "color 0.3s ease",
                  fontFamily: "inherit",
                }}
                onMouseEnter={(e) => (e.target.style.color = colors.tertiary)}
                onMouseLeave={(e) =>
                  (e.target.style.color =
                    activeSection === section
                      ? colors.tertiary
                      : colors.primary)
                }
              >
                {section}
                {activeSection === section && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "-3px",
                      left: 0,
                      right: 0,
                      height: "3px",
                      backgroundColor: colors.tertiary,
                    }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            style={{
              display: "block",
              background: "none",
              border: "none",
              color: colors.primary,
              cursor: "pointer",
              padding: "0.5rem",
            }}
            className="mobile-menu-btn"
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div
            style={{
              backgroundColor: colors.light,
              borderTop: `1px solid ${colors.light}`,
              padding: "2rem",
              display: "flex",
              flexDirection: "column",
              gap: "1.5rem",
            }}
            className="mobile-menu"
          >
            {["home", "about", "services", "contact"].map((section) => (
              <button
                key={section}
                onClick={() => scrollToSection(section)}
                style={{
                  background: "none",
                  border: "none",
                  color: colors.primary,
                  fontSize: "1.25rem",
                  fontWeight: "600",
                  cursor: "pointer",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  textAlign: "left",
                  padding: "0.75rem 0",
                  fontFamily: "inherit",
                }}
              >
                {section}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* Home Section */}
      <section
        id="home"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
          position: "relative",
          overflow: "hidden",
          paddingTop: "80px",
        }}
      >
        {/* Animated background orb 1 — large blue, top-right */}
        <div
          style={{
            position: "absolute",
            top: "5%",
            right: "0%",
            width: "600px",
            height: "600px",
            borderRadius: "50%",
            background: `radial-gradient(circle, ${colors.accent}35 0%, transparent 70%)`,
            filter: "blur(70px)",
            animation: "floatOrb1 9s ease-in-out infinite alternate",
          }}
        />
        {/* Animated background orb 2 — teal, bottom-left */}
        <div
          style={{
            position: "absolute",
            bottom: "0%",
            left: "-8%",
            width: "500px",
            height: "500px",
            borderRadius: "50%",
            background: `radial-gradient(circle, ${colors.tertiary}45 0%, transparent 70%)`,
            filter: "blur(80px)",
            animation: "floatOrb2 11s ease-in-out infinite alternate",
          }}
        />
        {/* Animated background orb 3 — mid accent, center */}
        <div
          style={{
            position: "absolute",
            top: "45%",
            left: "35%",
            width: "350px",
            height: "350px",
            borderRadius: "50%",
            background: `radial-gradient(circle, ${colors.accent}20 0%, transparent 70%)`,
            filter: "blur(55px)",
            animation: "floatOrb3 7s ease-in-out infinite alternate",
          }}
        />
        {/* Rotating ring 1 */}
        <div
          style={{
            position: "absolute",
            top: "12%",
            right: "12%",
            width: "320px",
            height: "320px",
            border: `1px solid ${colors.accent}25`,
            borderRadius: "50%",
            animation: "spinCW 22s linear infinite",
          }}
        />
        {/* Rotating ring 2 — inner */}
        <div
          style={{
            position: "absolute",
            top: "19%",
            right: "19%",
            width: "190px",
            height: "190px",
            border: `1px solid ${colors.tertiary}30`,
            borderRadius: "50%",
            animation: "spinCCW 15s linear infinite",
          }}
        />
        {/* Floating diamond */}
        <div
          style={{
            position: "absolute",
            bottom: "20%",
            right: "25%",
            width: "80px",
            height: "80px",
            border: `2px solid ${colors.accent}30`,
            animation:
              "spinCW 12s linear infinite, floatOrb3 6s ease-in-out infinite alternate",
          }}
        />
        {/* Small accent dot cluster — top-left */}
        <div
          style={{
            position: "absolute",
            top: "25%",
            left: "8%",
            width: "160px",
            height: "160px",
            border: `1px dashed ${colors.tertiary}20`,
            borderRadius: "50%",
            animation: "pulseRing 4s ease-in-out infinite",
          }}
        />

        <div
          style={{
            maxWidth: "1400px",
            margin: "0 auto",
            padding: "4rem 2rem",
            position: "relative",
            zIndex: 1,
          }}
        >
          <div
            style={{
              maxWidth: "900px",
            }}
          >
            <h1
              style={{
                fontSize: "clamp(2.5rem, 7vw, 5rem)",
                fontWeight: "900",
                lineHeight: "1.1",
                color: colors.white,
                marginBottom: "2rem",
                letterSpacing: "-0.02em",
                fontFamily: '"Ibarra Real Nova", serif',
              }}
            >
              Engineering Automation Without Limits.
              <br />
            </h1>

            <p
              style={{
                fontSize: "clamp(1.1rem, 2vw, 1.4rem)",
                lineHeight: "1.7",
                color: colors.white,
                marginBottom: "1.5rem",
                fontWeight: "300",
              }}
            >
              At BK Automation Limited, we design and build bespoke automation
              systems that solve complex industrial challenges. From custom
              robotics and intelligent control software to fully integrated
              machinery, our solutions are engineered around your exact
              operational needs.
            </p>

            <p
              style={{
                fontSize: "clamp(1rem, 1.8vw, 1.2rem)",
                lineHeight: "1.8",
                color: colors.light,
                opacity: 0.9,
                marginBottom: "3rem",
                fontWeight: "300",
              }}
            >
              We help manufacturers increase efficiency, enhance safety, and
              unlock new levels of productivity through precision-built,
              future-ready automation.
            </p>

            <button
              onClick={() => scrollToSection("contact")}
              style={{
                backgroundColor: colors.tertiary,
                color: colors.white,
                border: "none",
                padding: "1.25rem 3rem",
                fontSize: "1rem",
                fontWeight: "700",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                cursor: "pointer",
                borderRadius: "8px",
                transition: "all 0.3s ease",
                fontFamily: "inherit",
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = `0 10px 30px ${colors.tertiary}40`;
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "none";
              }}
            >
              Get Started
            </button>
          </div>
        </div>

        <button
          onClick={() => scrollToSection("about")}
          style={{
            position: "absolute",
            bottom: "2rem",
            left: "50%",
            transform: "translateX(-50%)",
            animation: "bounce 2s infinite",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "0.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          aria-label="Scroll to About section"
        >
          <ChevronDown size={32} color={colors.white} />
        </button>
      </section>

      {/* About Section */}
      <section
        id="about"
        style={{
          backgroundColor: colors.white,
          padding: "6rem 2rem",
        }}
      >
        <div
          style={{
            maxWidth: "1400px",
            margin: "0 auto",
            width: "100%",
          }}
        >
          {/* ── Row 1: text (left) + carousel (right) ── */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "4rem",
              alignItems: "center",
              marginBottom: "4rem",
            }}
          >
            {/* Text block */}
            <div>
              <div
                style={{
                  width: "60px",
                  height: "4px",
                  backgroundColor: colors.tertiary,
                  marginBottom: "1.5rem",
                }}
              />
              <h2
                style={{
                  fontSize: "clamp(2rem, 5vw, 3.5rem)",
                  fontWeight: "900",
                  lineHeight: "1.2",
                  color: colors.primary,
                  marginBottom: "2rem",
                  letterSpacing: "-0.02em",
                  fontFamily: '"Ibarra Real Nova", serif',
                }}
              >
                Built on
                <br />
                Expertise
              </h2>
              <p
                style={{
                  fontSize: "clamp(1rem, 1.8vw, 1.2rem)",
                  lineHeight: "1.8",
                  color: colors.secondary,
                }}
              >
                BK Automation Limited was founded by a team of experienced
                automation engineers with over 60 years of combined expertise in
                industrial automation, delivering reliable and innovative
                solutions to customers.
              </p>
            </div>

            {/* Carousel */}
            <div
              style={{
                position: "relative",
                borderRadius: "14px",
                overflow: "hidden",
                aspectRatio: "16/10",
                boxShadow: "0 8px 32px rgba(6,10,20,0.18)",
              }}
            >
              {/* Slides */}
              {carouselSlides.map((slide, index) => (
                <div
                  key={index}
                  style={{
                    position: "absolute",
                    inset: 0,
                    opacity: index === currentSlide ? 1 : 0,
                    transition: "opacity 0.8s ease-in-out",
                    backgroundColor: slide.bgColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={slide.url}
                    alt={slide.caption}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                  {/* Caption overlay */}
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background:
                        "linear-gradient(transparent, rgba(6,10,20,0.78))",
                      padding: "2rem 1.5rem 1.25rem",
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        color: colors.white,
                        fontSize: "0.95rem",
                        fontWeight: "600",
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                      }}
                    >
                      {slide.caption}
                    </p>
                  </div>
                </div>
              ))}

              {/* Prev button */}
              <button
                onClick={() =>
                  setCurrentSlide(
                    (prev) =>
                      (prev - 1 + carouselSlides.length) %
                      carouselSlides.length,
                  )
                }
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "rgba(255,255,255,0.18)",
                  backdropFilter: "blur(4px)",
                  border: "none",
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: colors.white,
                  fontSize: "1.1rem",
                  zIndex: 2,
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "rgba(255,255,255,0.34)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "rgba(255,255,255,0.18)")
                }
                aria-label="Previous slide"
              >
                ‹
              </button>

              {/* Next button */}
              <button
                onClick={() =>
                  setCurrentSlide((prev) => (prev + 1) % carouselSlides.length)
                }
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "rgba(255,255,255,0.18)",
                  backdropFilter: "blur(4px)",
                  border: "none",
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: colors.white,
                  fontSize: "1.1rem",
                  zIndex: 2,
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "rgba(255,255,255,0.34)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "rgba(255,255,255,0.18)")
                }
                aria-label="Next slide"
              >
                ›
              </button>

              {/* Dot indicators */}
              <div
                style={{
                  position: "absolute",
                  bottom: "14px",
                  right: "16px",
                  display: "flex",
                  gap: "6px",
                  zIndex: 2,
                }}
              >
                {carouselSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    style={{
                      width: index === currentSlide ? "20px" : "8px",
                      height: "8px",
                      borderRadius: "4px",
                      border: "none",
                      background: colors.white,
                      cursor: "pointer",
                      padding: 0,
                      transition: "all 0.3s ease",
                    }}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* ── Row 2: 4 stats full-width ── */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "1.5rem",
            }}
          >
            {[
              { number: "60+", label: "Years Combined Experience" },
              { number: "200+", label: "Projects Delivered" },
              { number: "15+", label: "Countries Served" },
              { number: "99.8%", label: "Uptime Achieved" },
            ].map((stat, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: colors.tertiary,
                  padding: "2.5rem 2rem",
                  textAlign: "center",
                  borderRadius: "12px",
                  transition: "transform 0.3s ease",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.transform = "translateY(-5px)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = "translateY(0)")
                }
              >
                <div
                  style={{
                    fontSize: "clamp(2rem, 4vw, 3rem)",
                    fontWeight: "900",
                    color: colors.white,
                    marginBottom: "0.5rem",
                    letterSpacing: "-0.02em",
                  }}
                >
                  {stat.number}
                </div>
                <div
                  style={{
                    fontSize: "0.9rem",
                    color: colors.white,
                    fontWeight: "600",
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    opacity: 0.8,
                  }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section
        id="services"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          backgroundColor: colors.light,
          padding: "6rem 2rem",
          position: "relative",
        }}
      >
        <div
          style={{
            maxWidth: "1400px",
            margin: "0 auto",
            width: "100%",
            position: "relative",
            zIndex: 1,
          }}
        >
          <div
            style={{
              textAlign: "center",
              marginBottom: "4rem",
            }}
          >
            <div
              style={{
                width: "60px",
                height: "4px",
                backgroundColor: colors.tertiary,
                margin: "0 auto 1.5rem",
              }}
            />

            <h2
              style={{
                fontSize: "clamp(2rem, 5vw, 3.5rem)",
                fontWeight: "900",
                lineHeight: "1.2",
                color: colors.primary,
                marginBottom: "1.5rem",
                letterSpacing: "-0.02em",
                fontFamily: '"Ibarra Real Nova", serif',
              }}
            >
              Our Services
            </h2>

            <p
              style={{
                fontSize: "clamp(1rem, 1.8vw, 1.2rem)",
                lineHeight: "1.7",
                color: colors.secondary,
                maxWidth: "700px",
                margin: "0 auto",
              }}
            >
              BK Automation Limited provides fully customised automation systems
              designed to meet unique production and operational requirements.
              Click any service to get in touch.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "2.5rem",
            }}
          >
            {[
              {
                title: "System Design",
                subtitle: "Motor Control Cabinet → Plant SCADA",
                description:
                  "System design is a critical step in ensuring project success. Through a collaborative design process, client objectives are clearly defined from the outset — allowing each project to be carefully planned and engineered end-to-end.",
                features: [
                  "Collaborative Client Design Process",
                  "Concept & Feasibility Planning",
                  "Technology Selection",
                  "Full Engineering Documentation",
                ],
                subject: "System Design – [Brief description of your project]",
              },
              {
                title: "SCADA Systems",
                subtitle: "Supervisory Control & Data Acquisition",
                description:
                  "Full-scope SCADA system development providing real-time visibility and control over your entire plant operation. We create intuitive operator interfaces and robust data management systems that empower your team with complete oversight.",
                features: [
                  "Real-Time Process Monitoring",
                  "Custom HMI Development",
                  "Data Logging & Reporting",
                  "Remote Access & Alarming",
                ],
                subject:
                  "SCADA Systems – [Brief description of your requirements]",
              },
              {
                title: "Electrical Designs",
                subtitle: "Hardware Design & Build",
                description:
                  "We provide comprehensive electrical and control system design using 3D and electrical CAD software. Our detailed design demonstration ensures clarity, accuracy and ease of use for electrical personell at all levels.",
                features: [
                  "Electrical Power & Control Schematics",
                  "Variable Speed Drive Schematics",
                  "PLC Rack & HML Layout",
                  "I/O Schematics",
                  "Network Layouts",
                ],
                subject:
                  "Electrical Designs – [Brief description of your requirements]",
              },
              {
                title: "Mechanical Design",
                subtitle: "Machine Design & Build",
                description:
                  "We provide mechanical design solution to meet your unique requirements. Whether you need to bring a new concept to life or overcome complex manufacturing challenges, our team develop bespoke design using advanced CAD tools and high quality components.",
                features: [
                  "Detailed Mechanical Design using 3D CAD",
                  "Detailed Documentation For Manufacturing",
                  "Detailed BOM",
                  "Complete End to End Solutions",
                ],
                subject:
                  "Mechanical Design – [Brief description of your requirements]",
              },
            ].map((service, index) => (
              <div
                key={index}
                onClick={() =>
                  handleServiceClick(service.title, service.subject)
                }
                style={{
                  backgroundColor: colors.primary,
                  padding: "2.5rem 2rem",
                  borderRadius: "12px",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  border: `2px solid transparent`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-8px)";
                  e.currentTarget.style.boxShadow = `0 20px 60px rgba(3,81,204,0.25)`;
                  e.currentTarget.style.borderColor = `${colors.accent}60`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.borderColor = "transparent";
                }}
              >
                <h3
                  style={{
                    fontSize: "clamp(1.3rem, 2.5vw, 1.75rem)",
                    fontWeight: "800",
                    color: colors.white,
                    marginBottom: "1rem",
                    letterSpacing: "-0.01em",
                    fontFamily: '"Ibarra Real Nova", serif',
                  }}
                >
                  {service.title}
                </h3>
                {/* Subtitle badge */}
                <div
                  style={{
                    display: "inline-block",
                    backgroundColor: `${colors.light}50`,
                    color: colors.light,
                    padding: "0.3rem 0.85rem",
                    fontSize: "0.75rem",
                    fontWeight: "700",
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    borderRadius: "4px",
                    marginBottom: "1rem",
                    alignSelf: "flex-start",
                  }}
                >
                  {service.subtitle}
                </div>
                <p
                  style={{
                    fontSize: "0.95rem",
                    lineHeight: "1.7",
                    color: colors.white,
                    marginBottom: "1.5rem",
                    opacity: 0.85,
                    flexGrow: 1,
                  }}
                >
                  {service.description}
                </p>

                <div
                  style={{
                    borderTop: `1px solid ${colors.tertiary}50`,
                    paddingTop: "1.25rem",
                    marginBottom: "1.5rem",
                  }}
                >
                  {service.features.map((feature, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                        marginBottom: "0.6rem",
                      }}
                    >
                      <div
                        style={{
                          width: "6px",
                          height: "6px",
                          backgroundColor: colors.light,
                          borderRadius: "50%",
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          fontSize: "0.9rem",
                          color: colors.white,
                          fontWeight: "500",
                          opacity: 0.9,
                        }}
                      >
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>

                {/* CTA row */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    color: colors.light,
                    fontSize: "0.875rem",
                    fontWeight: "700",
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    marginTop: "auto",
                  }}
                >
                  <span>Enquire about this service</span>
                  <span style={{ fontSize: "1.1rem" }}>→</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          backgroundColor: colors.secondary,
          padding: "6rem 2rem",
        }}
      >
        <div
          style={{
            maxWidth: "1400px",
            margin: "0 auto",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              gap: "4rem",
              alignItems: "start",
            }}
          >
            <div>
              <div
                style={{
                  width: "60px",
                  height: "4px",
                  backgroundColor: colors.tertiary,
                  marginBottom: "1.5rem",
                }}
              />

              <h2
                style={{
                  fontSize: "clamp(2rem, 5vw, 3.5rem)",
                  fontWeight: "900",
                  lineHeight: "1.2",
                  color: colors.white,
                  marginBottom: "2rem",
                  letterSpacing: "-0.02em",
                  fontFamily: '"Ibarra Real Nova", serif',
                }}
              >
                Let's Discuss
                <br />
                Your Project
              </h2>

              <p
                style={{
                  fontSize: "clamp(1rem, 1.8vw, 1.2rem)",
                  lineHeight: "1.8",
                  color: colors.white,
                  marginBottom: "3rem",
                }}
              >
                Ready to transform your operations with intelligent automation?
                Our team is standing by to discuss your specific requirements
                and develop a tailored solution.
              </p>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1.5rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                  }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: colors.tertiary,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "8px",
                    }}
                  >
                    <Mail size={20} color={colors.white} />
                  </div>
                  <span
                    style={{
                      color: colors.white,
                      fontSize: "1rem",
                    }}
                  >
                    info@bkautomationltd.com
                  </span>
                </div>
              </div>
            </div>

            <div
              style={{
                backgroundColor: colors.tertiary,
                padding: "3rem 2.5rem",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Light explosion overlay — same shape as the card */}
              {highlightForm && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    borderRadius: "10px",
                    background:
                      "radial-gradient(ellipse at center, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.55) 45%, rgba(255,255,255,0.12) 75%, transparent 100%)",
                    animation: "lightExplosion 0.5s ease-out forwards",
                    pointerEvents: "none",
                    zIndex: 0,
                  }}
                />
              )}
              <form
                onSubmit={handleSubmit}
                style={{ position: "relative", zIndex: 1 }}
              >
                <div style={{ marginBottom: "1.5rem" }}>
                  <label
                    style={{
                      display: "block",
                      color: colors.white,
                      fontSize: "0.875rem",
                      fontWeight: "700",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    style={{
                      width: "100%",
                      padding: "1rem",
                      backgroundColor: colors.white,
                      border: `2px solid ${colors.secondary}`,
                      color: colors.primary,
                      fontSize: "1rem",
                      outline: "none",
                      transition: "border-color 0.3s ease",
                      fontFamily: "inherit",
                      borderRadius: "8px",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = colors.white)}
                    onBlur={(e) =>
                      (e.target.style.borderColor = colors.secondary)
                    }
                  />
                </div>

                <div style={{ marginBottom: "1.5rem" }}>
                  <label
                    style={{
                      display: "block",
                      color: colors.white,
                      fontSize: "0.875rem",
                      fontWeight: "700",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Company *
                  </label>
                  <input
                    type="text"
                    name="company"
                    required
                    value={formData.company}
                    onChange={handleInputChange}
                    style={{
                      width: "100%",
                      padding: "1rem",
                      backgroundColor: colors.white,
                      border: `2px solid ${colors.secondary}`,
                      color: colors.secondary,
                      fontSize: "1rem",
                      outline: "none",
                      transition: "border-color 0.3s ease",
                      fontFamily: "inherit",
                      borderRadius: "8px",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = colors.white)}
                    onBlur={(e) =>
                      (e.target.style.borderColor = colors.secondary)
                    }
                  />
                </div>

                <div style={{ marginBottom: "1.5rem" }}>
                  <label
                    style={{
                      display: "block",
                      color: colors.white,
                      fontSize: "0.875rem",
                      fontWeight: "700",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    style={{
                      width: "100%",
                      padding: "1rem",
                      backgroundColor: colors.white,
                      border: `2px solid ${colors.secondary}`,
                      color: colors.secondary,
                      fontSize: "1rem",
                      outline: "none",
                      transition: "border-color 0.3s ease",
                      fontFamily: "inherit",
                      borderRadius: "8px",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = colors.white)}
                    onBlur={(e) =>
                      (e.target.style.borderColor = colors.secondary)
                    }
                  />
                </div>

                <div style={{ marginBottom: "1.5rem" }}>
                  <label
                    style={{
                      display: "block",
                      color: colors.white,
                      fontSize: "0.875rem",
                      fontWeight: "700",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleInputChange}
                    style={{
                      width: "100%",
                      padding: "1rem",
                      backgroundColor: colors.white,
                      border: `2px solid ${colors.secondary}`,
                      color: colors.secondary,
                      fontSize: "1rem",
                      outline: "none",
                      transition: "border-color 0.3s ease",
                      fontFamily: "inherit",
                      borderRadius: "8px",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = colors.white)}
                    onBlur={(e) =>
                      (e.target.style.borderColor = colors.secondary)
                    }
                  />
                </div>

                <div style={{ marginBottom: "2rem" }}>
                  <label
                    style={{
                      display: "block",
                      color: colors.white,
                      fontSize: "0.875rem",
                      fontWeight: "700",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Message *
                  </label>
                  <textarea
                    name="message"
                    required
                    rows="5"
                    value={formData.message}
                    onChange={handleInputChange}
                    style={{
                      width: "100%",
                      padding: "1rem",
                      backgroundColor: colors.white,
                      border: `2px solid ${colors.secondary}`,
                      color: colors.secondary,
                      fontSize: "1rem",
                      outline: "none",
                      transition: "border-color 0.3s ease",
                      resize: "vertical",
                      fontFamily: "inherit",
                      borderRadius: "8px",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = colors.white)}
                    onBlur={(e) =>
                      (e.target.style.borderColor = colors.secondary)
                    }
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    width: "100%",
                    backgroundColor: colors.white,
                    color: colors.tertiary,
                    border: "none",
                    padding: "1.25rem",
                    fontSize: "1rem",
                    fontWeight: "700",
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                    borderRadius: "8px",
                    transition: "all 0.3s ease",
                    opacity: isSubmitting ? 0.7 : 1,
                    fontFamily: "inherit",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSubmitting) {
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow = `0 10px 30px ${colors.white}40`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  {isSubmitting ? "Sending..." : "Send Request"}
                </button>

                {/* Feedback messages */}
                {submitStatus === "success" && (
                  <div
                    style={{
                      marginTop: "1.25rem",
                      padding: "1rem 1.25rem",
                      backgroundColor: "rgba(255,255,255,0.12)",
                      border: "1px solid rgba(255,255,255,0.3)",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                    }}
                  >
                    <span style={{ fontSize: "1.25rem" }}>✓</span>
                    <div>
                      <p
                        style={{
                          margin: 0,
                          color: colors.white,
                          fontWeight: "700",
                          fontSize: "0.95rem",
                        }}
                      >
                        Enquiry sent successfully!
                      </p>
                      <p
                        style={{
                          margin: "2px 0 0",
                          color: colors.white,
                          opacity: 0.8,
                          fontSize: "0.85rem",
                        }}
                      >
                        We'll be in touch shortly.
                      </p>
                    </div>
                  </div>
                )}

                {submitStatus === "error" && (
                  <div
                    style={{
                      marginTop: "1.25rem",
                      padding: "1rem 1.25rem",
                      backgroundColor: "rgba(220,50,50,0.15)",
                      border: "1px solid rgba(220,50,50,0.4)",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                    }}
                  >
                    <span style={{ fontSize: "1.25rem" }}>✕</span>
                    <div>
                      <p
                        style={{
                          margin: 0,
                          color: colors.white,
                          fontWeight: "700",
                          fontSize: "0.95rem",
                        }}
                      >
                        Something went wrong.
                      </p>
                      <p
                        style={{
                          margin: "2px 0 0",
                          color: colors.white,
                          opacity: 0.8,
                          fontSize: "0.85rem",
                        }}
                      >
                        Please email us directly at info@bkautomationltd.com
                      </p>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        style={{
          backgroundColor: colors.white,
          borderTop: `2px solid ${colors.light}`,
          padding: "2rem",
          textAlign: "center",
        }}
      >
        <p
          style={{
            color: colors.primary,
            fontSize: "0.95rem",
            letterSpacing: "0.05em",
          }}
        >
          © {new Date().getFullYear()} BK Automation Limited. All rights
          reserved.
        </p>
      </footer>

      {/* CSS Animations */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Ibarra+Real+Nova:wght@400;500;600;700&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        
        @keyframes bounce {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50% { transform: translateX(-50%) translateY(-10px); }
        }

        @keyframes floatOrb1 {
          0%   { transform: translate(0px, 0px); }
          100% { transform: translate(-70px, 50px); }
        }

        @keyframes floatOrb2 {
          0%   { transform: translate(0px, 0px); }
          100% { transform: translate(60px, -55px); }
        }

        @keyframes floatOrb3 {
          0%   { transform: translate(0px, 0px) scale(1); }
          100% { transform: translate(-45px, 35px) scale(1.15); }
        }

        @keyframes spinCW {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }

        @keyframes spinCCW {
          from { transform: rotate(360deg); }
          to   { transform: rotate(0deg); }
        }

        @keyframes pulseRing {
          0%, 100% { opacity: 0.25; transform: scale(1); }
          50%       { opacity: 0.6;  transform: scale(1.12); }
        }

        @keyframes lightExplosion {
          0%   { opacity: 0; }
          15%  { opacity: 1; }
          100% { opacity: 0; }
        }
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        html {
          scroll-behavior: smooth;
        }
        
        .nav-inner {
          padding: 0.6rem 1rem;
        }
        .nav-logo {
          height: 2.75rem;
          margin-top: 0.25rem;
        }

        @media (min-width: 768px) {
          .nav-inner {
            padding: 1rem 2rem;
          }
          .nav-logo {
            height: 4rem;
            margin-top: 0.5rem;
          }
          .desktop-nav {
            display: flex !important;
          }
          .mobile-menu-btn {
            display: none !important;
          }
        }

        @media (min-width: 1024px) {
          .nav-logo {
            height: 5rem;
            margin-top: 1rem;
          }
        }
      `}</style>
    </div>
  );
}
